import { Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { IconCacheService } from '@finxone-platform/shared/services';
import { ImageType, LogosResultTypeList, ProjectIcon } from '@finxone-platform/shared/sys-config-types';
import { Select } from '@ngxs/store';
import { BehaviorSubject, Observable, firstValueFrom, tap } from 'rxjs';
import {
  iconNameFromFileNameSvg,
  registerProjectIconWithUrl,
  registerProjectIconWithUrlAsync,
} from '../../../../../../../libs/shared/utils/src/icons/register-icons.utils';
import { ProjectsState, WorkflowProjectType } from '../../state/project.state';
import { ApiService } from '../api-service/api.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectIconsService {
  private _projectIconsList: ProjectIcon[] = [];
  public get projectIconsList(): ProjectIcon[] {
    return [...this._projectIconsList];
  }
  private activeProject: WorkflowProjectType;
  public projectIconsList$ = new BehaviorSubject<ProjectIcon[]>([]);
  private iconsLoadedSubject = new BehaviorSubject<boolean>(false);
  public iconsLoaded$ = this.iconsLoadedSubject.asObservable();
  @Select(ProjectsState.getActiveProject) activeProject$: Observable<WorkflowProjectType>;

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private apiService: ApiService,
    private iconCacheService: IconCacheService,
  ) {
    this.activeProject$
      .pipe(
        tap((activeProject) => {
          // when active project changes reload project icons
          this.activeProject = activeProject;
          this.addAllProjectIconsToList();
        }),
      )
      .subscribe();
  }

  public addProjectIconToList(icon: LogosResultTypeList) {
    const iconName = iconNameFromFileNameSvg(icon.filename);
    if (this.isIconNameAdded(iconName)) return; // already added so return blank

    registerProjectIconWithUrl(icon.path, iconName, this.matIconRegistry, this.domSanitizer);

    this._projectIconsList.push({
      ...icon,
      isRegisteredIcon: true,
      iconName,
      iconNamespace: 'project',
    });
  }

  public async addAllProjectIconsToList(): Promise<void> {
    this.clearProjectIcons();

    // no active project yet (initial state emission) — nothing to load
    if (!this.activeProject?.id) return;

    try {
      const imagesFromBlob = await firstValueFrom(
        this.apiService.onGetImages(this.activeProject.id, 1, ImageType.ICON),
      );

      const registerIconPromises = imagesFromBlob.result.map((icon: LogosResultTypeList) => {
        const iconName = iconNameFromFileNameSvg(icon.filename);

        if (!this.isIconNameAdded(iconName)) {
          this._projectIconsList.push({
            ...icon,
            isRegisteredIcon: true,
            iconName,
            iconNamespace: 'project',
          });
        }

        return registerProjectIconWithUrlAsync(icon.path, iconName, this.matIconRegistry, this.domSanitizer);
      });

      // Wait for all icons to be fetched and registered
      await Promise.all(registerIconPromises);

      this.projectIconsList$.next(this._projectIconsList);
      this.initialiseIconsForHandlebars(); // only called after icons are ready

      this.iconsLoadedSubject.next(true); // mark as loaded
    } catch (error) {
      console.error('Error loading project icons:', error);
      // still mark as settled: the whole builder UI is gated on iconsLoaded$
      // (ui-builder.component), and a failed icon fetch must not block it —
      // the builder just renders without project icons
      // Project icons are optional presentation data. An unavailable file service
      // must not keep the entire UI builder behind its iconsLoaded template gate.
      this.projectIconsList$.next([]);
      this.initialiseIconsForHandlebars();
      this.iconsLoadedSubject.next(true);
    }
  }

  private initialiseIconsForHandlebars() {
    const iconsForHandlebars: { name: string; url: string }[] = this._projectIconsList.map((icon) => {
      return {
        name: `${icon.iconNamespace}:${icon.iconName}`,
        url: icon.path,
      };
    });
    this.iconCacheService.initializeIconUrls(iconsForHandlebars);
  }

  private getSvgIconSetDefs(svg: string): SVGDefsElement | null {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svg, 'image/svg+xml');
    const defs = doc.querySelector('svg > defs');

    if (defs && defs.querySelector('[id]')) {
      return defs as SVGDefsElement;
    }

    return null;
  }

  public isIconNameAdded(iconName: string): boolean {
    return this._projectIconsList.some((icon) => icon.iconName === iconName);
  }

  public clearProjectIcons(): void {
    this._projectIconsList = [];
  }

  public removeIconByName(iconName: string): void {
    this._projectIconsList = this._projectIconsList.filter((i) => i.iconName !== iconName);
  }

  public removeIconById(iconId: string): void {
    this._projectIconsList = this._projectIconsList.filter((i) => i.id !== iconId);
  }
}
